<template>
  <div class="card-container focus-primary">
    <validation-observer ref="checkStep">
      <b-card-body>
        <b-form @submit.prevent="submit">
          <!-- IMEI -->
          <b-form-group class="imei-form-group">
            <validation-provider
                #default="{ errors }"
                name="IMEI"
                rules="required"
            >
              <div class="imei-wrapper">
                <!-- Brand hint (top-left, absolute) -->
                <span v-if="brandHint && !bulk" class="brand-hint">{{ brandHint }}</span>
                <b-input-group class="imei-group" :class="{
                  'has-error': !!err || errors.length > 0,
                  'has-value': bulk ? !!imei.multi : !!imei.single,
                }">
                  <b-input-group-prepend class="icon" is-text>
                    <feather-icon icon="SmartphoneIcon"/>
                  </b-input-group-prepend>
                  <!-- Multi Line -->
                  <b-form-textarea
                      v-if="bulk"
                      v-model="imei.multi"
                      class="input-imei"
                      placeholder="1 IMEI/SN per Line
Max 50.000 imeis

(if realtime process stuck,
 check the history page)"

                      rows="5"
                      max-rows="15"
                  />
                  <!--Single Line -->
                  <b-form-input
                      v-else
                      @input="insertImei"
                      :value="imei.single"
                      class="input-imei"
                      autocomplete="off"
                      maxlength="20"
                      inputmode="text"
                      placeholder="Enter IMEI/Serial "
                  />
                  <!-- Camera scan overlay (single mode only) -->
                  <ImeiScanner v-if="!bulk" class="imei-scan-overlay" @scanned="onScanned" />
                </b-input-group>
                <!-- Digit counter (bottom-right) -->
                <span v-if="imei.single && !bulk && !(imeiLen === 15 && !!err)" class="imei-counter" :class="{ complete: imeiLen === 15 && !err, over: imeiLen > 15 }">
                  {{ imeiLen === 15 && !err ? 'Valid' : imeiLen + '/15' }}
                </span>
                <span v-if="imei.multi && bulk" class="imei-counter bulk-counter">
                  {{ bulkCount }} IMEI{{ bulkCount !== 1 ? 's' : '' }}
                </span>
              </div>
              <small class="text-danger imei-error">{{ errors[0] }} {{ err }} </small>
            </validation-provider>
          </b-form-group>
          <!-- Services -->
          <validation-provider
              #default="{ errors }"
              name="service"
              rules="required"
          >
            <SmartSelect
                ref="smartSelect"
                v-model="service"
                :list="services"
                :brand="detectedBrand"
                icon="LayersIcon"
                placeholder="Select Service"
                label="name"
                :price="true"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form>
      </b-card-body>
      <b-card-footer class="footer-actions text-center">
        <b-button @click="submit" size="lg" variant="gradient-primary" pill>
          Check
        </b-button>
      </b-card-footer>
    </validation-observer>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BButton,
  BCardFooter,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
  BFormTextarea,
  BForm,
  BFormGroup,
} from 'bootstrap-vue'
import SmartSelect from '@/components/UI/SmartSelect.vue'
import ImeiScanner from '@/components/imei/ImeiScanner.vue'
import ServiceApi from '@/services/services.service.js'
import api from '@axios'
import {
  ValidationProvider,
  ValidationObserver,
  setInteractionMode,
} from 'vee-validate'
import { required, email } from '@validations'

setInteractionMode('passive')

export default {
  props: ['bulk'],
  data: () => ({
    err: null,
    imei: {
      single: null,
      multi: null,
    },
    service: null, // Service ID
    services: null,
    brandHint: null,
    detectedBrand: null,
    // validation rules
    required,
    email,
  }),
  computed: {
    imeiLen() {
      return (this.imei.single || '').length
    },
    bulkCount() {
      if (!this.imei.multi) return 0
      return this.imei.multi.split('\n').filter(l => l.trim().length > 0).length
    },
  },
  watch: {
    service(val) {
      const service = this.services?.find((item) => item.id == val)
      this.$emit('setService', service)
    },
    bulk(isBulk) {
      if (isBulk) {
        this.detectedBrand = null
        this.brandHint = null
        this.err = null
      }
    },
  },
  async mounted() {
    this.services = await ServiceApi.fetch()
    const ps = this.$route.params
    const serviceId = ps.service?.id
    this.service = Number.isInteger(serviceId) ? serviceId : null
    if (ps.bulk) {
      this.$emit('handleBulk', true)
      this.imei.multi = ps.imei
    } else {
      this.imei.single = ps.imei
    }
    // Brand check if IMEI already present
    if (this.imei.single && this.imei.single.length === 15 && this.valid_imei(this.imei.single)) {
      this.fetchBrand(this.imei.single)
    }
    const savedService = localStorage.getItem('saved_service')
    if (savedService && !this.service) {
      this.service = parseInt(savedService)
    }
  },
  methods: {

    valid_imei(value) {
// accept only digits, dashes or spaces
      if (/[^0-9-\s]+/.test(value)) return false
// The Luhn Algorithm. It's so pretty.
      var nCheck = 0,
          nDigit = 0,
          bEven = false
      value = value.replace(/\D/g, '')
      for (var n = value.length - 1; n >= 0; n--) {
        var cDigit = value.charAt(n),
            nDigit = parseInt(cDigit, 10)
        if (bEven) {
          if ((nDigit *= 2) > 9) nDigit -= 9
        }
        nCheck += nDigit
        bEven = !bEven
      }
      return (nCheck % 10) == 0
    },

    insertImei(value) {
      this.imei.single = value.toUpperCase()
          .replace(/[^a-zA-Z0-9]/g, '')

      if (this.imei.single.length == 15) {
        if (!this.valid_imei(this.imei.single)) {
          this.err = 'Invalid IMEI'
          this.brandHint = null
          this.detectedBrand = null
        } else {
          this.fetchBrand(this.imei.single)
        }
      }
      if (this.imei.single.length > 15) {
        this.err = 'IMEI cannot be longer than 15 digits.'
        this.brandHint = null
        this.detectedBrand = null
      }
      if (this.imei.single.length < 15) {
        this.err = null
        this.brandHint = null
        this.detectedBrand = null
      }
    },

    onScanned(imei) {
      this.imei.single = imei
      this.err = null
      this.brandHint = null
      this.detectedBrand = null
      if (imei.length === 15 && !this.valid_imei(imei)) {
        this.err = 'Invalid IMEI'
      } else if (imei.length === 15) {
        this.fetchBrand(imei)
      }
    },

    fetchBrand(imei) {
      this.brandHint = null
      this.detectedBrand = null
      api.get('/imei/brandCheck', { params: { imei } })
        .then(({ data }) => {
          if (data.result && this.imei.single === imei) {
            this.brandHint = data.result
            this.detectedBrand = data.brand || null
          }
        })
        .catch(() => {})
    },

    submit() {
      const imei = this.bulk ? this.imei.multi : this.imei.single
      this.$refs.checkStep.validate()
          .then(async (success) => {
            if (success) {
              this.$emit('submit', imei)
            }
          })
    },

    refreshUsage() {
      this.$refs.smartSelect?.refreshUsage()
    },
  },
  components: {
    BCard,
    BButton,
    BCardBody,
    BCardFooter,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    SmartSelect,
    BFormTextarea,
    BForm,
    BFormGroup,
    ValidationProvider,
    ValidationObserver,
    ImeiScanner,
  },
}
</script>

<style lang="scss" scoped>
.card-container {
  max-width: 500px;
  margin: 0 auto;
  @media (max-width: 576px) {
    max-width: 100%;
  }
}

// Wrapper — reliable position context for absolute children (iOS safe)
.imei-wrapper {
  position: relative;
  display: block;
  width: 100%;
  overflow: visible;
}

// Extra bottom space for counter + error messages
.imei-form-group {
  margin-bottom: 1.5rem;
  overflow: visible;
  // iOS: validation-provider renders as <span>, force block display
  > span {
    display: block;
    overflow: visible;
  }
}

.input-imei {
  border-left: none;
  text-align: center;
  font-size: 1.15rem;
  color: #1a1a1a;
  font-weight: 500;
}

// Focus glow on the input group
.imei-group {
  position: relative;
  overflow: visible !important;
  // Icon collapse when has value
  .input-group-text {
    transition: width 0.25s, padding 0.25s, opacity 0.2s, border 0.2s;
    overflow: hidden;
    white-space: nowrap;
  }
  &.has-value .input-group-text {
    width: 0 !important;
    padding: 0 !important;
    border: none !important;
    opacity: 0;
  }
  &.has-value .input-imei {
    border-left: 1px solid #d8d6de;
    border-radius: 7px !important;
  }
  .input-imei {
    padding-right: 40px; // room for camera icon
  }
}
.imei-group:focus-within {
  .form-control,
  .input-group-text {
    border-color: #7367f0;
  }
}
.imei-group.has-error {
  .form-control,
  .input-group-text {
    border-color: #ea5455;
  }
}

.imei-scan-overlay {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  -webkit-transform: translateY(-50%);
  z-index: 3;
}

// Digit counter
.imei-counter {
  position: absolute;
  right: 10px;
  bottom: -16px;
  font-size: 0.68rem;
  color: #b9b9c3;
  font-family: "SF Mono", "Fira Code", monospace;
  letter-spacing: 0.3px;
}
.imei-counter.complete { color: #28c76f; }
.imei-counter.over { color: #ea5455; }
.bulk-counter { right: 10px; bottom: -18px; font-size: 0.82rem; }

// Brand hint — absolute top-left, inside imei-wrapper
.brand-hint {
  position: absolute;
  top: -20px;
  left: 0;
  font-size: 0.78rem;
  color: #7367f0;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 75%;
  z-index: 2;
}

.imei-error {
  display: block;
  margin-top: 4px;
}

.footer-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: none !important;
  padding-top: 0;

  .action {
    display: flex;
    flex-direction: column;
  }
}

// Desktop: bigger, clearer inputs
@media (min-width: 768px) {
  .input-imei {
    font-size: 1.3rem !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
  .imei-group .input-group-text {
    padding-top: 10px;
    padding-bottom: 10px;
  }
}
</style>

<style lang="scss">
.dark-layout .card-container {
  .imei-group.has-value .input-imei {
    border-left-color: #404656;
  }
  .input-imei {
    color: #e2e2e2;
  }
}
</style>